import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { mapApiCatalogueItemToView } from "@/lib/liveCatalogueItemMap";
import { formatDtoolsMoney } from "@/lib/dtoolsJsonFormat";
import { trpc } from "@/lib/trpc";
import { buildDtoolsProjectCatalogKey } from "@shared/dtoolsProjectCatalogKey";
import {
  productPickIdentityKey,
  type DtoolsReviseProductPick,
  type SupportingComponentQuantityPick,
} from "@shared/dtoolsProposalRevise";
import { Loader2, Plus, Search, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

export type ExistingSupportingDuplicate = {
  identityKey: string;
  title: string;
  quantity: number;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Single-select (Add Product / Replace). Ignored when selectionMode is multi. */
  onSelect?: (product: DtoolsReviseProductPick) => void;
  /**
   * multi: select several products, review quantities, then confirm once.
   * single (default): existing Add behaviour.
   */
  selectionMode?: "single" | "multi";
  title?: string;
  description?: string;
  /** Shown when confirming multi-select supporting components. */
  onConfirmMulti?: (picks: SupportingComponentQuantityPick[]) => void;
  /** Existing supporting components under the parent, keyed by catalogue identity. */
  existingDuplicates?: ExistingSupportingDuplicate[];
};

const SEARCH_HELPER =
  "Search Project Catalogue or Retail Catalogue by brand, model, SKU or keyword.";
const SEARCH_PLACEHOLDER = "Search brand, model, SKU or keywords…";

type SelectedRow = {
  identityKey: string;
  product: DtoolsReviseProductPick;
  quantity: number;
};

function pickFromProjectItem(item: {
  id: number;
  brand: string | null;
  model: string | null;
  polishedDescription: string | null;
  originalDtoolsDescription: string | null;
  avgQuotedPrice: number | string | null;
  imageUrl: string | null;
  catalogueKey: string | null;
  dtoolsCatalogId: string | null;
  dtoolsComponentId: string | null;
  category: string | null;
  pricingMethod?: string | null;
  defaultPercentage?: string | number | null;
}): DtoolsReviseProductPick {
  const brand = item.brand ?? null;
  const model = item.model ?? null;
  const catalogueKey =
    item.catalogueKey ??
    buildDtoolsProjectCatalogKey({
      manufacturer: brand,
      model,
      catalogId: item.dtoolsCatalogId,
      componentId: item.dtoolsComponentId,
    });
  return {
    brand,
    model,
    displayName: [brand, model].filter(Boolean).join(" ") || "Product",
    description:
      String(item.polishedDescription ?? item.originalDtoolsDescription ?? "").trim() || "Product",
    unitPrice: Number(item.avgQuotedPrice ?? 0) || 0,
    imageUrl: item.imageUrl,
    catalogueKey,
    projectCatalogueId: item.id,
    catalogMatchConfidence: "matched",
    category: item.category ?? null,
    pricingMethod:
      String(item.pricingMethod ?? "") === "percentage_of_equipment"
        ? "percentage_of_equipment"
        : null,
    percentageOfEquipment: (() => {
      const raw = item.defaultPercentage;
      if (raw == null || raw === "") return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    })(),
  };
}

function pickFromRetailItem(item: {
  catalogueItemId?: number | null;
  displayName: string;
  brand?: string | null;
  model?: string | null;
  sku?: string | null;
  shortDescription?: string | null;
  retailPrice: number;
  imageUrl?: string | null;
  category?: string | null;
  pricingMethod?: string | null;
  defaultPercentage?: number | string | null;
}): DtoolsReviseProductPick {
  const catalogueItemId = item.catalogueItemId ?? null;
  return {
    brand: item.brand ?? null,
    model: item.model ?? null,
    displayName: item.displayName,
    description: String(item.shortDescription ?? item.displayName).trim(),
    unitPrice: item.retailPrice,
    imageUrl: item.imageUrl,
    retailCatalogueItemId: catalogueItemId,
    catalogProductId: catalogueItemId,
    catalogMatchConfidence: catalogueItemId != null && catalogueItemId > 0 ? "matched" : null,
    catalogueKey: buildDtoolsProjectCatalogKey({
      manufacturer: item.brand,
      model: item.model,
      catalogId: item.sku,
      displayName: item.displayName,
    }),
    sku: item.sku ?? null,
    category: item.category ?? null,
    pricingMethod:
      item.pricingMethod === "percentage_of_equipment" ? "percentage_of_equipment" : null,
    percentageOfEquipment:
      item.pricingMethod === "percentage_of_equipment"
        ? item.defaultPercentage != null
          ? Number(item.defaultPercentage)
          : null
        : null,
  };
}

export default function DtoolsReviseProductPickerDialog({
  open,
  onOpenChange,
  onSelect,
  selectionMode = "single",
  title,
  description,
  onConfirmMulti,
  existingDuplicates = [],
}: Props) {
  const isMulti = selectionMode === "multi";
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [selected, setSelected] = useState<SelectedRow[]>([]);
  const [phase, setPhase] = useState<"browse" | "review">("browse");

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setSelected([]);
      setPhase("browse");
    }
  }, [open]);

  const projectQ = trpc.salesSandbox.listProjectCatalog.useQuery(
    { search: debounced || undefined, limit: 40 },
    { enabled: open, retry: false },
  );
  const retailQ = trpc.catalogue.listForSales.useQuery(
    { search: debounced, sort: "most_used" as const },
    { enabled: open, retry: false },
  );

  const projectItems = projectQ.data?.items ?? [];
  const retailItems = useMemo(
    () =>
      (retailQ.data?.items ?? []).map((row) =>
        mapApiCatalogueItemToView(row as Parameters<typeof mapApiCatalogueItemToView>[0]),
      ),
    [retailQ.data?.items],
  );

  const selectedKeys = useMemo(() => new Set(selected.map((row) => row.identityKey)), [selected]);
  const duplicateByKey = useMemo(() => {
    const map = new Map<string, ExistingSupportingDuplicate>();
    for (const row of existingDuplicates) map.set(row.identityKey, row);
    return map;
  }, [existingDuplicates]);

  const dialogTitle =
    title ??
    (isMulti ? "Add Supporting Components" : phase === "review" ? "Review selection" : "Add product");

  const toggleProduct = (product: DtoolsReviseProductPick) => {
    const identityKey = productPickIdentityKey(product) ?? `tmp:${product.displayName}:${product.unitPrice}`;
    setSelected((prev) => {
      if (prev.some((row) => row.identityKey === identityKey)) {
        return prev.filter((row) => row.identityKey !== identityKey);
      }
      return [...prev, { identityKey, product, quantity: 1 }];
    });
  };

  const setQuantity = (identityKey: string, quantity: number) => {
    const qty = Math.max(1, Math.round(Number(quantity) || 1));
    setSelected((prev) =>
      prev.map((row) => (row.identityKey === identityKey ? { ...row, quantity: qty } : row)),
    );
  };

  const confirmMulti = () => {
    if (!onConfirmMulti || selected.length === 0) return;
    onConfirmMulti(
      selected.map((row) => ({
        product: row.product,
        quantity: Math.max(1, Math.round(row.quantity) || 1),
      })),
    );
    onOpenChange(false);
  };

  const renderProductRow = (opts: {
    key: string;
    title: string;
    subtitle: string;
    product: DtoolsReviseProductPick;
  }) => {
    const identityKey =
      productPickIdentityKey(opts.product) ?? `tmp:${opts.product.displayName}:${opts.product.unitPrice}`;
    const checked = selectedKeys.has(identityKey);
    const existing = duplicateByKey.get(identityKey);

    if (!isMulti) {
      return (
        <li key={opts.key} className="flex items-center justify-between gap-3 px-4 py-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{opts.title}</p>
            <p className="line-clamp-2 text-xs text-muted-foreground">{opts.subtitle}</p>
          </div>
          <Button
            type="button"
            size="sm"
            className="shrink-0"
            data-testid="product-picker-add"
            onClick={() => {
              onSelect?.(opts.product);
              onOpenChange(false);
            }}
          >
            <Plus className="mr-1 h-3.5 w-3.5" /> Add
          </Button>
        </li>
      );
    }

    return (
      <li key={opts.key} className="flex items-start gap-3 px-4 py-3">
        <Checkbox
          checked={checked}
          onCheckedChange={() => toggleProduct(opts.product)}
          className="mt-1"
          data-testid={`product-picker-check-${identityKey}`}
          aria-label={`Select ${opts.title}`}
        />
        <button
          type="button"
          className="min-w-0 flex-1 text-left"
          onClick={() => toggleProduct(opts.product)}
        >
          <p className="truncate text-sm font-medium">{opts.title}</p>
          <p className="line-clamp-2 text-xs text-muted-foreground">{opts.subtitle}</p>
          {existing ? (
            <p className="mt-1 text-xs text-amber-800" data-testid={`product-picker-already-${identityKey}`}>
              Already added — current quantity: {existing.quantity}. Confirming will increase quantity.
            </p>
          ) : null}
        </button>
      </li>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[85vh] max-w-2xl flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl"
        data-testid="dtools-revise-product-picker"
      >
        <div className="shrink-0 space-y-3 border-b px-6 pb-3 pt-6 pr-14" data-testid="product-picker-chrome">
          <DialogHeader className="space-y-0 text-left">
            <DialogTitle>{dialogTitle}</DialogTitle>
          </DialogHeader>
          <DialogDescription
            className="text-sm text-muted-foreground"
            data-testid="product-picker-helper"
          >
            {description ?? SEARCH_HELPER}
          </DialogDescription>
          {phase === "browse" ? (
            <div className="relative" data-testid="product-picker-search">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                className="h-10 pl-10"
                placeholder={SEARCH_PLACEHOLDER}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="product-picker-search-input"
              />
            </div>
          ) : null}
        </div>

        {phase === "browse" ? (
          <Tabs defaultValue="project" className="flex min-h-0 flex-1 flex-col">
            <div className="shrink-0 border-b px-6 py-3" data-testid="product-picker-tabs">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="project">Project Catalogue</TabsTrigger>
                <TabsTrigger value="retail">Retail Catalogue</TabsTrigger>
              </TabsList>
            </div>
            <TabsContent
              value="project"
              className="mt-0 min-h-0 flex-1 overflow-y-auto px-2 pb-4 data-[state=inactive]:hidden"
              data-testid="product-picker-results-project"
            >
              {projectQ.isLoading ? (
                <p className="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                </p>
              ) : projectItems.length === 0 ? (
                <p className="px-4 py-6 text-sm text-muted-foreground">No project catalogue matches.</p>
              ) : (
                <ul className="divide-y">
                  {projectItems.map((item) => {
                    const product = pickFromProjectItem(item);
                    return renderProductRow({
                      key: String(item.id),
                      title: [item.brand, item.model].filter(Boolean).join(" ") || "Product",
                      subtitle: String(
                        item.polishedDescription || item.originalDtoolsDescription || "",
                      ),
                      product,
                    });
                  })}
                </ul>
              )}
            </TabsContent>
            <TabsContent
              value="retail"
              className="mt-0 min-h-0 flex-1 overflow-y-auto px-2 pb-4 data-[state=inactive]:hidden"
              data-testid="product-picker-results-retail"
            >
              {retailQ.isLoading ? (
                <p className="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                </p>
              ) : retailItems.length === 0 ? (
                <p className="px-4 py-6 text-sm text-muted-foreground">No retail catalogue matches.</p>
              ) : (
                <ul className="divide-y">
                  {retailItems.map((item) => {
                    const product = pickFromRetailItem(item);
                    return renderProductRow({
                      key: String(item.catalogueItemId ?? item.displayName),
                      title: item.displayName,
                      subtitle: [
                        [item.brand, item.model].filter(Boolean).join(" · "),
                        item.sku ? `SKU ${item.sku}` : null,
                        formatDtoolsMoney(item.retailPrice),
                      ]
                        .filter(Boolean)
                        .join(" · "),
                      product,
                    });
                  })}
                </ul>
              )}
            </TabsContent>
          </Tabs>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4" data-testid="product-picker-review">
            <p className="mb-3 text-sm font-medium text-[#1A2744]">Selected Supporting Components</p>
            <ul className="space-y-3">
              {selected.map((row) => {
                const existing = duplicateByKey.get(row.identityKey);
                return (
                  <li
                    key={row.identityKey}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3"
                    data-testid={`product-picker-review-row-${row.identityKey}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">{row.product.displayName}</p>
                      <p className="text-xs text-muted-foreground tabular-nums">
                        {formatDtoolsMoney(row.product.unitPrice)} each
                      </p>
                      {existing ? (
                        <p className="mt-1 text-xs text-amber-800">
                          Already added — current quantity: {existing.quantity}. Confirming increases by{" "}
                          {row.quantity}.
                        </p>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-muted-foreground" htmlFor={`qty-${row.identityKey}`}>
                        Quantity
                      </label>
                      <Input
                        id={`qty-${row.identityKey}`}
                        className="h-8 w-20"
                        type="number"
                        min={1}
                        value={row.quantity}
                        onChange={(e) => setQuantity(row.identityKey, Number(e.target.value))}
                        data-testid={`product-picker-qty-${row.identityKey}`}
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="text-destructive"
                        onClick={() =>
                          setSelected((prev) => prev.filter((r) => r.identityKey !== row.identityKey))
                        }
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
            <p className="mt-3 text-sm text-muted-foreground" data-testid="product-picker-selected-count">
              {selected.length} product{selected.length === 1 ? "" : "s"} selected
            </p>
          </div>
        )}

        {isMulti ? (
          <DialogFooter className="shrink-0 border-t px-6 py-3 sm:justify-between">
            <div className="text-sm text-muted-foreground" data-testid="product-picker-selected-count">
              {selected.length} selected
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              {phase === "browse" ? (
                <Button
                  type="button"
                  disabled={selected.length === 0}
                  data-testid="product-picker-review-button"
                  onClick={() => setPhase("review")}
                >
                  Review {selected.length > 0 ? `(${selected.length})` : ""}
                </Button>
              ) : (
                <>
                  <Button type="button" variant="outline" onClick={() => setPhase("browse")}>
                    Back
                  </Button>
                  <Button
                    type="button"
                    disabled={selected.length === 0}
                    data-testid="product-picker-confirm-multi"
                    onClick={confirmMulti}
                  >
                    Add {selected.length} Supporting Component{selected.length === 1 ? "" : "s"}
                  </Button>
                </>
              )}
            </div>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
